import MUIButton from '@mui/material/Button';
import styled from 'styled-components';

import Flex from 'quarks/Flex';
import type { FlexProps } from 'quarks/interpolations/flex';
import { flex } from 'quarks/interpolations/flex';
import { allCSSWithPseudos } from 'quarks/styleProps/all';

import type { ButtonTrackingSchema } from 'components/ComponentButton/buttonTypeConfig';

import parseUrl from 'utils/parseUrl';

import type { FC, MouseEvent, ReactNode } from 'react';

const StyledButton = styled(MUIButton).withConfig({
  shouldForwardProp: prop => !Object.keys(allCSSWithPseudos).includes(prop),
})`
  &&& {
    ${flex}
  }
`;

export interface ButtonProps extends FlexProps {
  /**
   * Pass the label as a child. Works with a string or icon.
   */
  children: ReactNode;
  /**
   * ReactNode of icon to be put at the end of the button
   */
  endIcon?: ReactNode;
  /**
   * ReactNode of icon to be put at the beginning of the button
   */
  startIcon?: ReactNode;
  /**
   * Choose the horizontal size of the icon, with height set automatically. Takes any valid CSS width.
   */
  iconSize?: string;
  /**
   * Determines between an outlined, text, or contained button
   */
  variant?: 'contained' | 'text' | 'outlined';
  /**
   * The URL to link to when the button is clicked. If defined, an `a` element will be used as the root node.
   */
  href?: string;
  /**
   * If `true`, the button is disabled.
   */
  disabled?: boolean;
  /**
   * The size of the component.
   */
  size?: 'medium' | 'large' | 'xlarge' | '2xlarge';
  /**
   * Callback fired when the button is clicked. Also fires on `space bar` and `enter` key presses when focused.
   * event: The event source of the callback. You can pull out the new value by accessing `event.target.value` (string).
   */
  onClick?: (event: MouseEvent) => void;
  /**
   * If set, it is rendered as this element.
   */
  component?: string;
  /**
   * Sets the button or anchor tag `type` attribute.
   */
  type?: 'button' | 'submit' | 'reset' | HTMLAnchorElement['type'];
  color?: 'primary' | 'secondary' | 'error' | 'success' | 'warning';
}

export const handleButtonTracking = (trackingSchema: ButtonTrackingSchema) => {
  window.analytics.track(
    'Button Clicked',
    {
      label: trackingSchema?.label || 'Unnamed Button',
      location: trackingSchema?.location ? trackingSchema.location : 'Unnamed Component',
      url: window.location.href,
      hierarchy: trackingSchema?.hierarchy ? trackingSchema.hierarchy : 'Unknown Location',
    },
    {
      context: {
        traits: window?.analytics?.user()?.traits(),
      },
    },
  );
};

const Button: FC<ButtonProps> = ({ children, endIcon, startIcon, href, iconSize, type, ...props }) => {
  const { as, ...urlProps } = href ? parseUrl(href) : ({ as: undefined } as const);

  return (
    <StyledButton
      {...urlProps}
      disableRipple
      textDecoration="none"
      forwardedAs={as}
      startIcon={
        startIcon && (
          <Flex width={iconSize} alignItems="center">
            {startIcon}
          </Flex>
        )
      }
      endIcon={
        endIcon && (
          <Flex width={iconSize} alignItems="center">
            {endIcon}
          </Flex>
        )
      }
      {...props}
      type={type || 'button'}
    >
      {children}
    </StyledButton>
  );
};

export default Button;

Button.defaultProps = {
  size: 'medium',
  disabled: false,
  width: 'fit-content',
  justifyContent: 'center',
  iconSize: 'fit-content',
};
